import { connect } from 'react-redux';
import MultiSelect from 'components/common/MultiSelectCheckbox/BinderV4Field';
import ErrorMessage from 'components/common/ErrorMessage';
import { datasourceOverrider, getOverriderNoDefault, getOverride } from 'common/util/binderHelpers';
import { useEffect, useState } from 'react';
import { chain, clone, differenceBy, filter, get, isArray, isEmpty } from 'lodash';
import DocumentDrivingDetailIcon from 'components/common/DocumentDrivingDetailIcon/index';
import { manageStudyMetadataPredicate } from 'common/permissions/studyPredicates';
import { getBinderV4Essentials } from 'pages/study/utils/studyUtils';
import { PageTitle } from 'components/common/Study/PageComponents';
import {
    getStudyTypesByInstitution,
    getStudyStages,
    saveStudy,
    clearStudy,
    discardStudy,
} from 'pages/study/actions/createStudyV2';
import styles from './StudySettingsDetails.scss';
import SaveFixedFooter from 'pages/studyOverview/components/SaveFixedFooter';

import {
    clearStudyNotifiedUsers,
    loadStudyNotifiedUsers,
    saveStudyNotifiedUsers,
    saveStudyEssentials,
    updateStudyDetails
} from 'pages/studyOverview/actions/studySettings';

import {
    isIntegrationToggleEnabled,
    partnerName,
    subscribeToConnexusStudy,
    unsubscribeToConnexusStudy
} from 'common/actions/integrationActions';

import LoadingComponent from 'common/permissions/components/LoadingComponent';
import { Fragment } from 'react';
import { getStudyMetadata } from 'pages/study/actions/createStudyV2';

const SPONSOR = {
    label: 'Sponsor',
    name: 'sponsor'
};

const TRIAL_ID = {
    label: 'Protocol Number / Trial id',
    name: 'trialId'
};

function StudySettingDetails({
    selectedDatasourceOverrides: overrides,
    dataSources,
    canAddDataSource,
    studyDetails,
    institutionId,
    studyMetadata,
    isLoading,
    updateStudyDetails,
    loadStudyNotifiedUsers,
    studyId,
    studyTeamMembers,
    studyNotifiedUsers,
    integrations,
    descriptors,
    saveStudyNotifiedUsers,
    saveStudyEssentials,
    canManageStudyMetadata,
    getStudyMetadata,
    studyType,
    ...props
}) {
    const [listOfFields, setListOdFields] = useState([]);
    const [restOfStudyDetails, setRestOfStudyDetails] = useState([]);
    const [metaDataToAdd, setMetadataToAdd] = useState([]);
    const [binder, setBinder] = useState(props.binder);
    const [notifyUsers, setNotifyUsers] = useState([]);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [isSiteId, setIsSiteId] = useState(true);
    const [isConnexusID, setIsConnexusId] = useState(true);
    const [notifyUsersChanged, setNotifyUsersChanged] = useState(false);
    const [metadataToRemove, setMetadataToRemove] = useState([]);
    const [emptyFields, setEmptyFields] = useState({});
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        loadStudyNotifiedUsers(studyId);
        setLoaded(true);

        return () => setLoaded(false);
    }, []);

    useEffect(() => {
        const [rest] = getDetails();
        setRestOfStudyDetails(rest);
    }, [studyDetails, dataSources]);

    useEffect(() => {
        const [, empty] = getDetails();
        if (isLoading || loaded) {
            setEmptyFields(empty);
        }
    }, [isLoading, loaded]);

    useEffect(() => {
        if (studyType && studyType.id) {
            const { id, binderTemplateId } = studyType;
            getStudyMetadata(institutionId, binderTemplateId, id);
        }
    }, []);

    function getDetails() {
        const emptyField = {};

        const sponsorOverrider = getOverride(SPONSOR.label, overrides, dataSources);
        const protocolTrialOverrider = getOverride(TRIAL_ID.label, overrides, dataSources);

        let rest = studyDetails.filter(detail => {
            if (sponsorOverrider && detail.dataSourceId === sponsorOverrider.id) {
                return false;
            } else if (protocolTrialOverrider && detail.dataSourceId === protocolTrialOverrider.id) {
                return false;
            }

            return true;
        });

        if (rest.length) {
            rest = rest.sort((a, b) => {
                const nameA = a.name.toUpperCase();
                const nameB = b.name.toUpperCase();
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }

                return 0;
            });

            rest.map((r, index) => {
                dataSources.map(dt => {
                    if (r.dataSourceId === dt.id) {
                        r.options = dt.parameters.values.map(el => ({ ...el, chipLabel: el.value, text: el.value }));
                    }
                });

                let value = studyMetadata.filter(meta => meta.descriptorId === r.descriptorId);

                if (value.length) {
                    emptyField[r.descriptorId] = false;
                    if (value[0].inputFieldType !== 'multi-select') {
                        r.value = value[0];
                    }

                    if (value[0].inputFieldType === 'multi-select') {
                        const uniqueChipLabels = new Set();

                        const updatedValue = value.map(el => {
                            if (!uniqueChipLabels.has(el.value)) {
                                uniqueChipLabels.add(el.value);
                                return { ...el, chipLabel: el.value, text: el.value };
                            } else {
                                return null;
                            }
                        }).filter(el => el !== null);

                        r.chipData = updatedValue;
                    }

                } else {
                    emptyField[r.descriptorId] = true;
                }

                if (!r.dataSourceId && r.inputFieldType.includes('single')) {
                    r.inputFieldType = '';
                }

            });

        }
        return [rest, emptyField];
    }

    useEffect(() => {
        const essentials = getBinderV4Essentials();
        //Identify document driving details  and remove them
        const documentDrivingDetailsOverride = overrides.filter(dt => restOfStudyDetails.some(el => el.dataSourceId === dt.dataSourceId));

        //Remove already displayed overrides
        let clearedFields = essentials.filter(field => !documentDrivingDetailsOverride.some(el => el.label === field.label));

        //Sponsor and trail id were already diplay in step 1, lets remove them
        const list = clearedFields.slice(2);

        const fieldsWithDatasourceId = list.map(field => {
            let [value] = studyMetadata.filter(el => el.name === field.name);
            const inputFieldType = hasOverride(field.label) || field.inputFieldType.includes('date') ? field.inputFieldType : '';

            const valueFromBinder = binder[field.old_baseName];
            if (!value && !!valueFromBinder) {
                value = { value: valueFromBinder };
            }
            return {
                ...field,
                ...value,
                inputFieldType
            };
        });

        setListOdFields(fieldsWithDatasourceId);

    }, [studyMetadata, restOfStudyDetails]);

    function getOptionsBySelector(selector) {
        const [dataSourceValues] = dataSources.filter(dt => dt.name === selector);

        let value = dataSourceValues && dataSourceValues.parameters && dataSourceValues.parameters.values;


        if (!isEmpty(value)) {
            return value.map(el => ({ ...el, chipLabel: el.text }));
        }

        return [];
    }

    function getDataSourceIdBySelector(selector) {
        const [dataSourceValues] = dataSources.filter(dt => dt.name === selector);

        if (dataSourceValues) {
            return dataSourceValues.id;
        }
    }

    function getOverridesLabel(selector) {
        const overridingLabel = datasourceOverrider(selector, overrides, dataSources);

        if (!overridingLabel) {
            return selector;
        }

        return overridingLabel;
    }

    function hasOverride(selector) {
        const override = getOverriderNoDefault(selector, overrides, dataSources);
        return !!override;
    }

    function fieldType(field) {
        let type = 'input';
        const isOverride = overrides.some(el => el.label === field);

        if (isOverride) {
            type = 'select';
        }

        return type;
    }

    function formatUserDropDownOptions(members = []) {
        return members.map(member => ({
            value: member.displayName,
            label: member.displayName,
            id: member.id,
        }));
    }

    function getPlaceHolder(selector) {
        const prefix = fieldType(selector) === 'select' ? 'Select' : 'Enter';

        return `${prefix} ${getOverridesLabel(selector)}`;
    }

    function handleDetailsOnChange(field, value) {
        const dataToRemove = studyMetadata.filter(metas => metas.name === field.name);
        setMetadataToRemove([...dataToRemove, ...metadataToRemove]);

        const cleanmetas = metaDataToAdd.filter(meta => meta.name !== field.name);

        const getType = (field) => props.staffDescriptor.descriptorId === field.descriptorId ? props.staffDescriptor.type : 'binder';
        const { name, descriptorId, dataType } = field;
        const currentType = getType(field);
        const inputFieldType = field.inputFieldType ? field.inputFieldType : 'single-value';

        if (isArray(value)) {
            if (field.descriptorId && value.length) {
                setEmptyFields({
                    ...emptyFields,
                    [field.descriptorId]: false
                });
            } else if (field.descriptorId && !value.length) {
                setEmptyFields({
                    ...emptyFields,
                    [field.descriptorId]: true
                });
            }
            const result = extractMultiSelectValues(value, field.name);
            if (!result) {
                value.forEach(el => {
                    const { dataType, descriptorId, inputFieldType, name } = field;
                    cleanmetas.push({ dataType, descriptorId, inputFieldType, name, institutionId, value: el.value, type: currentType });
                });
            } else {
                result.forEach(el => cleanmetas.push(el));
            }
        } else {
            if (field.descriptorId && value) {
                setEmptyFields({
                    ...emptyFields,
                    [field.descriptorId]: false
                });
            } else if (field.descriptorId && !value) {
                setEmptyFields({
                    ...emptyFields,
                    [field.descriptorId]: true
                });
            }
            cleanmetas.push({ name, institutionId, descriptorId, dataType, value: value.value ? value.value : value, type: currentType, inputFieldType });
        }

        setMetadataToAdd(cleanmetas);

        if (field.shouldBeAllValuesSelected) {
            updateStudyDetails(cleanmetas, dataToRemove);
        }
    }

    function extractMultiSelectValues(value, name) {
        const currentBase = props.staffDescriptor;

        if (!currentBase) return false;

        const rest = value.map(el => {
            const {
                value,
            } = el;
            return {
                dataType: currentBase.dataType || 'small_text',
                descriptorId: currentBase.descriptorId,
                inputFieldType: currentBase.inputFieldType,
                institutionId: currentBase.institutionId,
                name: currentBase.name,
                type: currentBase.type,
                value,
            };

        });

        return rest;
    }

    function handleSubmit() {
        const dataToRemove = studyMetadata.filter(meta => metaDataToAdd.some(toAdd => toAdd.name === meta.name));
        if (metadataToRemove.length > 0) {
            metadataToRemove.forEach(el => dataToRemove.push(el));
        }
        updateStudyDetails(metaDataToAdd, dataToRemove);
        saveStudyEssentials(binder);
        if (!notifyUsersChanged) return;

        const addedNotifiedUsers = differenceBy(notifyUsers, studyNotifiedUsers, 'id');
        const removedNotifiedUsers = differenceBy(studyNotifiedUsers, notifyUsers, 'id');
        if (!isEmpty(addedNotifiedUsers) || !isEmpty(removedNotifiedUsers)) {
            saveStudyNotifiedUsers({
                studyId,
                addedUserIds: addedNotifiedUsers.map(user => user.id),
                removedUserIds: removedNotifiedUsers.map(user => user.id),
            });
        }
    }

    function validateIntegrationFields() {
        const { connexusSiteId, connexusStudyId } = binder.partnerMetadata;
        const isSiteId = Boolean(connexusSiteId);
        const isConnexus = Boolean(connexusStudyId);
        setIsSiteId(isSiteId);
        setIsConnexusId(isConnexus);

        return isSiteId && isConnexus;
    }

    async function handleOnConnexusSubscrive() {
        if (!validateIntegrationFields()) return;

        const { subscribeToConnexusStudy, unsubscribeToConnexusStudy } = props;

        setIsSubscribing(true);

        let subscribeSucceeded = false;


        if (binder.partnerMetadata.connexusSubscribed) {
            const response = await unsubscribeToConnexusStudy({ binder });
            subscribeSucceeded = response ? response.succeeded : false;
        } else {
            const response = await subscribeToConnexusStudy({ binder });
            subscribeSucceeded = response ? response.succeeded : false;
        }

        if (subscribeSucceeded) {
            const binderClone = clone(binder);
            binderClone.partnerMetadata['connexusSubscribed'] = !binder.partnerMetadata.connexusSubscribed;
            setBinder(binderClone);
        }

        setIsSubscribing(false);
    }

    function handleIntegrationChange(id, value) {
        const binderClone = clone(binder);
        binderClone.partnerMetadata[id] = value;

        setBinder(binderClone);
    }

    function handleNotifyUsersOnChanges(value) {
        setNotifyUsersChanged(true);
        setNotifyUsers(value);
    }

    if (isLoading) {
        return <LoadingComponent />;
    }

    return (
        <div className={styles.StudySettingsDetails}>

            <PageTitle>Details</PageTitle>
            {
                restOfStudyDetails && restOfStudyDetails.map((field, index) => (
                    <MultiSelect
                        key={`${index}_${field.name}`}
                        label={field.name}
                        placeholder={`Select ${field.name}`}
                        options={field.options}
                        onSelectedOption={(value) => handleDetailsOnChange(field, value)}
                        value={field.value && field.value.value}
                        chipData={field.chipData}
                        Tooltip={() => DocumentDrivingDetailIcon(field.name, emptyFields[field.descriptorId], field.shouldBeAllValuesSelected)}
                        canAddDataSource={canAddDataSource}
                        type={field.inputFieldType}
                        showCreateField={true}
                        listWidth={'700px'}
                        datasourceId={get(field, 'dataSourceId', null)}
                        isSearchable={true}
                        isReadOnly={!canManageStudyMetadata}
                        instructions={field.instructions}
                        shouldBeAllValuesSelected={field.shouldBeAllValuesSelected}
                    />
                ))
            }
            {
                listOfFields.length && listOfFields.map((field, index) => (
                    <MultiSelect
                        key={`${index}_${field.label}`}
                        label={getOverridesLabel(field.label)}
                        options={getOptionsBySelector(getOverridesLabel(field.label))}
                        placeholder={`${getPlaceHolder(field.label)}`}
                        canAddDataSource={canAddDataSource}
                        showCreateField={true}
                        value={field.value || ''}
                        onChange={(value) => handleDetailsOnChange(field, value)}
                        onSelectedOption={(value) => handleDetailsOnChange(field, value)}
                        type={field.inputFieldType}
                        listWidth={'700px'}
                        datasourceId={getDataSourceIdBySelector(getOverridesLabel(field.label))}
                        isSearchable={true}
                        isReadOnly={!canManageStudyMetadata}
                    />
                ))
            }
            {
                isIntegrationToggleEnabled(integrations, partnerName.VELOS) && (
                    <MultiSelect
                        key="velos-study-id-integration"
                        label="Velos Integration Study ID"
                        placeholder="Enter Velos Integration Study ID"
                        value={binder.partnerMetadata.velosStudyId}
                        onChange={(value) => handleIntegrationChange('velosStudyId', value)}
                        isReadOnly={!canManageStudyMetadata}
                    />
                )
            }

            {
                isIntegrationToggleEnabled(integrations, partnerName.CONNEXUS) && (
                    <Fragment>
                        <MultiSelect
                            key="notify-users-integration"
                            label="Notify Users (Integration)"
                            options={formatUserDropDownOptions(studyTeamMembers)}
                            placeholder="Select User"
                            chipData={formatUserDropDownOptions(studyNotifiedUsers)}
                            onSelectedOption={handleNotifyUsersOnChanges}
                            type='multi-select'
                            listWidth={'700px'}
                            isSearchable={true}
                            isReadOnly={!canManageStudyMetadata}
                        />
                        <MultiSelect
                            key="connexus-integration-details"
                            label="Connexus Integration Details"
                            placeholder="Enter Connexus Integration Details"
                            value={binder.partnerMetadata.connexusStudyId}
                            inputType='number'
                            displayBtn={true}
                            notValid={!isConnexusID}
                            btnLabel={
                                isSubscribing ?
                                    `${binder.partnerMetadata.connexusSubscribed ? 'Unsubscribing' : 'Subscribing'}` :
                                    `${binder.partnerMetadata.connexusSubscribed ? 'Unsubscribe' : 'Subscribe'}`}
                            onBtnClicked={handleOnConnexusSubscrive}
                            onChange={(value) => handleIntegrationChange('connexusStudyId', value)}
                            isReadOnly={!canManageStudyMetadata}
                        />
                        {!isSiteId && (
                            <ErrorMessage text='In order to subscribe to connexus Connexus Integration Details' />
                        )}

                        <MultiSelect
                            key="connexus-site-id"
                            label="Site ID"
                            placeholder="Enter Site ID"
                            value={binder.partnerMetadata.connexusSiteId}
                            inputType='number'
                            notValid={!isSiteId}
                            onChange={(value) => handleIntegrationChange('connexusSiteId', value)}
                            isReadOnly={!canManageStudyMetadata}
                        />
                        {!isSiteId && (
                            <ErrorMessage text='In order to subscribe to connexus Site ID' />
                        )}
                    </Fragment>
                )
            }

            {canManageStudyMetadata && <SaveFixedFooter
                onClick={handleSubmit}
                data-cy="StudySettingsEssentials-onSubmit"
            >
                Save Changes
            </SaveFixedFooter>}
        </div>
    );
}

export default connect(
    (state, props) => {
        const binder = get(state, 'binder', []);
        const integrations = get(state, 'binder.integrations', []);
        const studyCreator = get(state, 'study.createdByUser');
        const institutionId = get(state, 'me.institution.id');
        const canAddDataSource = get(state, `me.institutionPermissions.${institutionId}.dataSources`, false);
        const canManageStudyMetadata = manageStudyMetadataPredicate(state);
        const studyTeam = get(state, 'studySettings.studyTeam', []);
        const studyTeamMembers = chain(studyTeam).map(member => member.user).concat([studyCreator]).value();
        const studyNotifiedUsers = get(state, 'studySettings.studyNotifiedUsers', []);
        const descriptors = get(state, 'studySettings.binderDescriptors', []);

        return {
            canAddDataSource,
            canManageStudyMetadata,
            institutionId,
            studyTeamMembers,
            studyNotifiedUsers,
            integrations,
            binder,
            descriptors,
            isLoading: get(state, 'study.loading'),
            studyId: get(state, 'study.id', null),
            binderId: get(state, 'study.binderId', null),
            dataSources: filter(state.dataSources, ['institutionId', institutionId]),
            selectedDatasourceOverrides: get(state, 'selectedDatasourceOverrides', []),
            studyDetails: get(state, 'study.studyDetails', []),
            studyType: get(state, 'study.studyType'),
            studyMetadata: get(state, 'study.metadata'),
            staffDescriptor: get(state, 'study.staffDescriptor', {}),
            binderDescritor: get(state, 'study.binderDescriptor', {}),
        };
    }, {
        getStudyTypesByInstitution,
        getStudyStages,
        saveStudy,
        clearStudy,
        discardStudy,
        updateStudyDetails,
        clearStudyNotifiedUsers,
        loadStudyNotifiedUsers,
        saveStudyNotifiedUsers,
        isIntegrationToggleEnabled,
        subscribeToConnexusStudy,
        unsubscribeToConnexusStudy,
        saveStudyEssentials,
        getStudyMetadata
    }
)(StudySettingDetails);